import {
  ExternalLinkIcon,
  LoadingIcon,
  MoreVerticalIcon,
  PlayIcon,
  SquareIcon,
  TrashIcon,
} from '@assets/icons';
import { Button, CopyIconButton, IconButton } from '@components/button';
import { Group } from '@components/layout';
import { InternalLink } from '@components/link';
import { useConfirmation, useModal } from '@components/modal';
import { Tooltip } from '@components/tooltip';
import { Typography } from '@components/typography';
import {
  RemoteDevelopmentApiOutput,
  useDeleteRemoteDevelopmentMutation,
  useGetRemoteDevelopmentSessionsQuery,
  useGetSshKeysQuery,
  useStartRemoteDevelopmentMutation,
  useStopRemoteDevelopmentMutation,
} from '@generated/UseGraphqlHooks';
import { Box, Divider, Menu, Stack } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import { CreateRemoteSessionModal } from '@subsets/profile/CreateRemoteSessionModal';
import { format, formatDistanceToNow, parseJSON } from 'date-fns';
import { upperFirst } from 'lodash';
import posthog from 'posthog-js';
import { useState } from 'react';
import { LaunchSessionOptions, RemoteSessionOptions } from './channels/RemoteSessionOptions';
import { RemoteSessionStatus } from './channels/RemoteSessionStatus';

const SessionRow = ({
  session,
  hasSSHKeys,
  refetch,
}: {
  session: RemoteDevelopmentApiOutput;
  hasSSHKeys: boolean;
  refetch: () => void;
}) => {
  const {
    channel,
    channelId,
    createdAt,
    createdBy,
    editorSessionId,
    editorUrl,
    instanceType,
    organization,
    organizationId,
    sshPort,
    status,
    updatedAt,
  } = session;
  const { showConfirmation } = useConfirmation();
  const { useAsyncNotification } = useNotifications();
  const [startSession] = useStartRemoteDevelopmentMutation();
  const [stopSession] = useStopRemoteDevelopmentMutation();
  const [deleteSession] = useDeleteRemoteDevelopmentMutation();
  const [launchAnchorEl, setLaunchAnchorEl] = useState<null | HTMLElement>(null);
  const onSessionStart = async () => {
    posthog.capture('user_start_remote-session', { channelId, editorSessionId });
    posthog.capture('user_launch_remote-session', { channelId, editorSessionId });
    await startSession({ variables: { editorSessionId } });
    refetch();
  };
  const onSessionStop = async () => {
    posthog.capture('user_stop_remote-session', { channelId, editorSessionId });
    await stopSession({ variables: { editorSessionId } });
    refetch();
  };
  const onSessionDelete = async () => {
    posthog.capture('user_delete_remote-session', { channelId, editorSessionId });
    await showConfirmation({
      onAffirm: useAsyncNotification('Remote session deleted', async () => {
        await deleteSession({ variables: { editorSessionId } });
        refetch();
      }),
      message: `Are you sure you want to delete the remote session: ${editorSessionId}?`,
    });
  };

  const [optionsMenuAnchorEl, setOptionsMenuAnchorEl] = useState<null | HTMLElement>(null);

  const allowedActions = new Set();
  if (status?.state === 'RUNNING') {
    allowedActions.add('stop');
    allowedActions.add('launch');
  }
  if (status?.state === 'STOPPED') {
    allowedActions.add('start');
  }
  if (status?.state !== 'DELETED') {
    allowedActions.add('delete');
    allowedActions.add('invite');
  }
  return (
    <>
      <Typography variant="body2" color="text.paper">
        {editorSessionId}
      </Typography>
      <Group gap={1} alignItems="center">
        <CopyIconButton value={channelId} />
        <Typography variant="body2" color="text.paper">
          {channel}
        </Typography>
      </Group>
      <Group gap={1} alignItems="center">
        <CopyIconButton value={organizationId} />
        <Typography variant="body2" color="text.paper">
          {organization}
        </Typography>
      </Group>
      <Group gap={1} alignItems="center">
        <Typography variant="body2" color="text.paper">
          {instanceType}
        </Typography>
      </Group>
      <Group gap={1} alignItems="center">
        <Typography
          variant="body2"
          color="text.paper"
          tooltip={format(parseJSON(createdAt), 'PPpp')}
        >
          {formatDistanceToNow(parseJSON(createdAt), { includeSeconds: true })} ago
        </Typography>
      </Group>
      <Group gap={1} alignItems="center">
        <Typography
          variant="body2"
          color="text.paper"
          tooltip={format(parseJSON(updatedAt), 'PPpp')}
        >
          {formatDistanceToNow(parseJSON(updatedAt), { includeSeconds: true })} ago
        </Typography>
      </Group>
      <Group gap={1} alignItems="center">
        <Typography variant="body2" color="text.paper">
          {createdBy}
        </Typography>
      </Group>
      <Group gap={2} alignItems="center">
        <RemoteSessionStatus session={session} />
        <Typography variant="body2" color="text.paper" sx={{ width: '10ch' }}>
          {upperFirst(status?.state.toLocaleLowerCase())}
        </Typography>
      </Group>
      <Group gap={1} alignItems="center" justifyContent="flex-end">
        {allowedActions.has('start') && (
          <Tooltip title="Start session" enterDelay={500} enterNextDelay={500}>
            <IconButton Icon={PlayIcon} onClick={onSessionStart} />
          </Tooltip>
        )}
        {allowedActions.has('launch') && (
          <>
            <Tooltip title="Launch session" enterDelay={500} enterNextDelay={500}>
              <IconButton
                Icon={ExternalLinkIcon}
                onClick={(e) => setLaunchAnchorEl(e.currentTarget)}
              />
            </Tooltip>
            <Menu
              open={Boolean(launchAnchorEl)}
              onClose={() => setLaunchAnchorEl(null)}
              anchorEl={launchAnchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <LaunchSessionOptions
                channelId={channelId}
                editorSessionId={editorSessionId}
                editorUrl={editorUrl}
                hasSSHKeys={hasSSHKeys}
                status={status}
                sshPort={sshPort}
                onClose={() => setLaunchAnchorEl(null)}
              />
            </Menu>
          </>
        )}
        {allowedActions.has('stop') && (
          <Tooltip title="Stop session" enterDelay={500} enterNextDelay={500}>
            <IconButton Icon={SquareIcon} onClick={onSessionStop} />
          </Tooltip>
        )}
        {allowedActions.has('delete') && (
          <Tooltip title="Delete session" enterDelay={500} enterNextDelay={500}>
            <IconButton Icon={TrashIcon} onClick={onSessionDelete} />
          </Tooltip>
        )}
        <IconButton
          Icon={MoreVerticalIcon}
          onClick={(e) => setOptionsMenuAnchorEl(e.currentTarget)}
        />
        <Menu
          open={Boolean(optionsMenuAnchorEl)}
          onClose={() => setOptionsMenuAnchorEl(null)}
          anchorEl={optionsMenuAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <RemoteSessionOptions
            session={session}
            channelId={channelId}
            onClose={() => setOptionsMenuAnchorEl(null)}
          />
        </Menu>
      </Group>
    </>
  );
};

export const RemoteSessions = () => {
  const { data, loading, refetch } = useGetRemoteDevelopmentSessionsQuery({
    pollInterval: 5000,
    fetchPolicy: 'no-cache',
  });

  const { showModal: showCreateModal } = useModal({
    component: CreateRemoteSessionModal,
    componentProps: {
      onClose: () => {
        refetch();
      },
    },
    modalProps: {
      title: 'Create remote session',
    },
  });

  const { data: sshKeyData } = useGetSshKeysQuery();

  const hasSshKeys = sshKeyData?.getSSHKeys?.length > 0;

  return (
    <Stack pt={10} pl={10} spacing={10} height="100%">
      <Group justifyContent="space-between" alignItems="center" gap={2}>
        <Typography maxWidth="400px" variant="h1">
          Remote Sessions
        </Typography>
        <Group alignItems="center" gap={2}>
          <InternalLink unstyled to="/profile?tab=keys">
            <Button variant="secondary" sx={{ backgroundColor: 'background.paper' }}>
              Manage SSH keys
            </Button>
          </InternalLink>
          <Button variant="accent" onClick={() => showCreateModal()}>
            Create session
          </Button>
        </Group>
      </Group>
      <Box sx={{ p: 4, backgroundColor: 'background.paper', overflow: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto auto auto auto auto auto auto auto 120px',
            columnGap: 8,
            gridAutoRows: 'auto',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              px: 2,
              display: 'grid',
              gridColumn: 'span 9',
              gridTemplateColumns: 'subgrid',
              alignItems: 'center',
            }}
          >
            <Typography variant="caption1">Session id</Typography>
            <Typography variant="caption1">Channel</Typography>
            <Typography variant="caption1">Organization</Typography>
            <Typography variant="caption1">Instance type</Typography>
            <Typography variant="caption1">Created</Typography>
            <Typography variant="caption1">Last modified</Typography>
            <Typography variant="caption1">Created by</Typography>
            <Typography variant="caption1">Status</Typography>
          </Box>
          <Divider sx={{ gridColumn: 'span 9', marginY: 2 }} />
          {loading ? (
            <Box sx={{}}>
              <LoadingIcon />
            </Box>
          ) : !(data?.listRemoteDevelopment?.length > 0) ? (
            <Typography>No remote sessions</Typography>
          ) : (
            <>
              {data?.listRemoteDevelopment.map((session) => (
                <Box
                  key={session.editorSessionId}
                  sx={{
                    py: 1,
                    px: 2,
                    display: 'grid',
                    gridColumn: 'span 9',
                    gridTemplateColumns: 'subgrid',
                    alignItems: 'center',
                    '&:hover': { backgroundColor: 'backgroundHover.paper' },
                  }}
                >
                  <SessionRow session={session} hasSSHKeys={hasSshKeys} refetch={refetch} />
                </Box>
              ))}
            </>
          )}
        </Box>
      </Box>
    </Stack>
  );
};
